// PasteModal — collects the pasted policy text before launching the audit.
//
// Phase A.4 (Mistral migration, 2026-06): the cookies + impressum paste
// fields were retired. They fed the OpenAI section graders that were
// removed in Phase A.4 (cookies grader's output was already discarded;
// the impressum supplement was an evidence-string append for the deleted
// swiss grader). The new Mistral pipeline analyses ONLY the privacy
// paste; cookies/impressum pastes would be silently ignored, so we
// stopped asking for them.
//
// A single field with a clear "if you don't have one, paste the closest
// document" hint replaces the three-field collapsible UI. Surface-only
// (URL-only with no paste) escape hatch is unchanged.

import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ClipboardPaste } from "lucide-react";

export interface PasteValues {
  privacy: string;
  // Kept on the shape for backend back-compat (the orchestrator still
  // accepts these from any legacy caller); the UI no longer collects them.
  cookies: string;
  impressum: string;
}

interface PasteModalProps {
  open: boolean;
  url: string;
  onSubmit: (values: PasteValues) => void;
  onCancel: () => void;
  /**
   * Called when the user explicitly chooses to run a URL-only surface scan
   * without pasting any policy text. Always reachable — no min-char gate.
   * The backend fully supports the URL-only path (honest "no policy detected"
   * + observed cookies/trackers).
   */
  onProceedWithoutPaste: () => void;
}

const PASTE_MIN_CHARS = 200;
// Backward-compat alias for existing tests that reference the old name.
const PRIVACY_MIN_CHARS = PASTE_MIN_CHARS;

export function PasteModal({
  open,
  url,
  onSubmit,
  onCancel,
  onProceedWithoutPaste,
}: PasteModalProps) {
  const [privacy, setPrivacy] = useState("");
  const [submitError, setSubmitError] = useState("");
  // Short/empty paste is NOT blocked — clicking the primary CTA with
  // insufficient text flips this to show an inline click-through notice
  // (continue with a surface-only scan, or go back and paste).
  const [confirmSurface, setConfirmSurface] = useState(false);

  const privacyTrimmed = privacy.trim();
  const privacyLen = privacyTrimmed.length;
  const anyFieldMeetsMin = privacyLen >= PASTE_MIN_CHARS;

  let privacyHint: string;
  if (privacyLen === 0) {
    privacyHint = `Minimum ${PASTE_MIN_CHARS} caractères.`;
  } else if (privacyLen < PASTE_MIN_CHARS) {
    const remaining = PASTE_MIN_CHARS - privacyLen;
    privacyHint = `Encore ${remaining} caractère${remaining > 1 ? "s" : ""} pour valider.`;
  } else {
    privacyHint = `${privacyLen.toLocaleString("fr-CH")} caractères : prêt pour l'analyse.`;
  }

  const handleSubmit = () => {
    setSubmitError("");
    if (!anyFieldMeetsMin) {
      // Empty / short paste: don't block. Surface-only is a valid path.
      setConfirmSurface(true);
      return;
    }
    onSubmit({
      privacy: privacyTrimmed,
      // Always empty — the backend still accepts them but the new
      // pipeline doesn't read them; we stopped collecting from the UI.
      cookies: "",
      impressum: "",
    });
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) onCancel();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">
            Collez votre politique de confidentialité
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            Pour{" "}
            <span className="font-medium text-foreground break-all">{url}</span>
            {" "}: collez le texte de votre politique de confidentialité ci-dessous.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div className="space-y-2">
            <label
              htmlFor="paste-modal-privacy"
              className="block text-sm font-semibold"
            >
              Politique de confidentialité
              <span className="ml-2 font-normal text-muted-foreground">
                · si vous n'en avez pas, collez votre document le plus proche
                (mentions légales, page de protection des données, conditions
                générales)
              </span>
            </label>
            <Textarea
              id="paste-modal-privacy"
              data-testid="paste-modal-privacy"
              placeholder="Collez ici le texte de votre politique de confidentialité."
              value={privacy}
              onChange={(e) => {
                setPrivacy(e.target.value);
                setSubmitError("");
              }}
              className="min-h-[200px] text-sm"
              maxLength={200_000}
              aria-required="true"
              aria-describedby="paste-modal-privacy-hint"
            />
            <p
              id="paste-modal-privacy-hint"
              className={`text-xs ${privacyLen > 0 && privacyLen < PRIVACY_MIN_CHARS ? "text-destructive" : "text-muted-foreground"}`}
            >
              {privacyHint}
            </p>
          </div>

          {submitError && (
            <p
              role="alert"
              className="text-sm text-destructive flex items-center gap-1.5"
            >
              {submitError}
            </p>
          )}
        </div>

        {confirmSurface ? (
          /* Click-through notice — NOT a block. The user knowingly proceeds
             with a surface-only scan, or goes back to paste a policy. */
          <div
            role="alertdialog"
            aria-label="Confirmer une analyse de surface"
            data-testid="paste-modal-confirm-surface"
            className="rounded-md border border-amber-300 bg-amber-50 p-4 space-y-3"
          >
            <p className="text-sm text-foreground leading-relaxed">
              Vous n'avez pas collé de politique (ou le texte est trop
              court). L'analyse portera <strong>uniquement sur les
              cookies, traceurs et sous-traitants observés</strong> sur
              votre site et signalera l'absence de politique exploitable.
              La revue complète clause par clause nécessite de coller votre
              politique.
            </p>
            <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setConfirmSurface(false)}
                data-testid="paste-modal-back-to-paste"
              >
                Revenir et coller ma politique
              </Button>
              <Button
                type="button"
                onClick={onProceedWithoutPaste}
                data-testid="paste-modal-confirm-continue"
                className="gap-2"
              >
                Continuer : analyse de surface
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <DialogFooter className="flex-col-reverse sm:flex-row gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              data-testid="paste-modal-cancel"
            >
              Annuler
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              data-testid="paste-modal-submit"
              className="gap-2"
            >
              <ClipboardPaste className="h-4 w-4" />
              Lancer le diagnostic
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default PasteModal;
